import axios from 'axios'
import {useCallback, useEffect, useMemo, useState} from 'react'
import {Alert, Badge, Button, Form, Offcanvas} from 'react-bootstrap'
import {toast} from 'react-toastify'

import {
  TELEPHONY_DRAWER_CLOSE_EVENT,
  TELEPHONY_DRAWER_OPEN_EVENT,
  TELEPHONY_DRAWER_STATE_EVENT,
  TELEPHONY_DRAWER_TOGGLE_EVENT,
  TELEPHONY_DRAWER_UPDATE_EVENT,
  TelephonyDrawerOpenDetail,
  TelephonyDrawerDiagnostics,
  TelephonyDrawerStateDetail,
  TELEPHONY_VICIDIAL_LOGOUT_EVENT,
} from '../../types/telephony'
import {URL} from '../../gql/client'
import {STORAGE_NAMES} from '../../helpers/consts'
import {useTelephony} from '../../hooks/useTelephony'

const VICIDIAL_EXTERNAL_WINDOW_KEY = '__policytoolsVicidialWebphoneWindow'

export const TelephonyDrawer = () => {
  const telephony = useTelephony()
  const [show, setShow] = useState(false)
  const [collapsed, setCollapsed] = useState(false)
  const [disconnecting, setDisconnecting] = useState(false)
  const [embeddedWebphoneUrl, setEmbeddedWebphoneUrl] = useState('')
  const [pauseCodeInput, setPauseCodeInput] = useState('')
  const [dtmfDigits, setDtmfDigits] = useState('')
  const [transferPhoneNumber, setTransferPhoneNumber] = useState('')
  const [runningAction, setRunningAction] = useState('')
  const [drawerState, setDrawerState] = useState<TelephonyDrawerOpenDetail>({
    url: '',
    title: 'Webphone',
  })

  const isVicidialAutologinBridge = useMemo(() => {
    return (
      drawerState.provider === 'VICIDIAL' &&
      drawerState.url.includes('/api/telephony/vicidial/autologin')
    )
  }, [drawerState.provider, drawerState.url])

  const shouldForceExternalOpen = useMemo(() => {
    if (!drawerState.url || typeof window === 'undefined') return false
    return window.location.protocol !== 'https:' && drawerState.url.startsWith('https://')
  }, [drawerState.url])

  const canOperateVicidial = useMemo(() => {
    return (
      drawerState.provider === 'VICIDIAL' &&
      telephony.config.vicidial_enabled &&
      Boolean(telephony.config.vicidial_agent_user)
    )
  }, [drawerState.provider, telephony.config.vicidial_agent_user, telephony.config.vicidial_enabled])

  const publishDrawerState = useCallback(
    (mounted: boolean, visible: boolean, provider?: TelephonyDrawerStateDetail['provider']) => {
      window.dispatchEvent(
        new CustomEvent<TelephonyDrawerStateDetail>(TELEPHONY_DRAWER_STATE_EVENT, {
          detail: {
            mounted,
            visible,
            provider,
          },
        })
      )
    },
    []
  )

  const openInNewTab = () => {
    const targetUrl = embeddedWebphoneUrl || drawerState.url
    if (!targetUrl) return
    const externalWindow = window.open(targetUrl, 'PolicyToolsVicidialWebphone')
    if (externalWindow) {
      ;(window as any)[VICIDIAL_EXTERNAL_WINDOW_KEY] = externalWindow
      externalWindow.focus?.()
    }
  }

  const closeTrackedExternalWindow = () => {
    let externalWindow = (window as any)[VICIDIAL_EXTERNAL_WINDOW_KEY] as Window | null | undefined
    if (!externalWindow) {
      externalWindow = window.open('', 'PolicyToolsVicidialWebphone')
    }
    if (externalWindow && !externalWindow.closed && externalWindow.location.href !== 'about:blank') {
      externalWindow.close()
    }
    ;(window as any)[VICIDIAL_EXTERNAL_WINDOW_KEY] = null
  }

  const handleVicidialLogout = async () => {
    const token = localStorage.getItem(STORAGE_NAMES.TOKEN)
    if (!token) {
      toast.error('Your session expired. Log in again to disconnect VICIdial.')
      return
    }

    setDisconnecting(true)
    try {
      await axios.post(
        `${URL}/api/telephony/disconnect-agent`,
        {provider: 'VICIDIAL'},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      )

      localStorage.removeItem(STORAGE_NAMES.VICIDIAL_SESSION)
      localStorage.setItem(STORAGE_NAMES.VICIDIAL_FORCE_DISCONNECTED, '1')
      window.dispatchEvent(new CustomEvent(TELEPHONY_VICIDIAL_LOGOUT_EVENT))
      
      const channel = new BroadcastChannel('policytools_vicidial_channel')
      channel.postMessage('CLOSE_WEBPHONE')
      channel.close()
      
      closeTrackedExternalWindow()
      setCollapsed(false)
      setShow(false)
      toast.info(
        shouldForceExternalOpen
          ? 'VICIdial disconnected. If the secure VICIdial page still remains open in another browser tab, use the native LOGOUT link there once.'
          : 'VICIdial disconnected.'
      )
    } catch (error: any) {
      const message = error?.response?.data?.error || 'Unable to log out from VICIdial.'
      toast.error(message)
    } finally {
      setDisconnecting(false)
    }
  }

  const runVicidialDrawerAction = async (actionKey: string, action: () => Promise<unknown>) => {
    setRunningAction(actionKey)
    try {
      await action()
    } finally {
      setRunningAction('')
    }
  }

  const handlePauseAgent = async () => {
    await runVicidialDrawerAction('pause', () =>
      telephony.setVicidialPause(true, pauseCodeInput)
    )
  }

  const handleResumeAgent = async () => {
    await runVicidialDrawerAction('resume', () => telephony.setVicidialPause(false))
  }

  const handleSendDtmf = async () => {
    const digits = dtmfDigits.trim()
    if (!digits) {
      toast.error('Enter DTMF digits first.')
      return
    }

    const response = await telephony.sendVicidialDtmf(digits)
    if (response) {
      setDtmfDigits('')
    }
  }

  const handleTransferCall = async () => {
    const phoneNumber = transferPhoneNumber.trim()
    if (!phoneNumber) {
      toast.error('Enter a transfer phone number first.')
      return
    }

    const response = await telephony.transferVicidialCall(phoneNumber)
    if (response) {
      setTransferPhoneNumber('')
    }
  }

  useEffect(() => {
    if (telephony.vicidialSession.pauseCode) {
      setPauseCodeInput(telephony.vicidialSession.pauseCode)
    }
  }, [telephony.vicidialSession.pauseCode])

  useEffect(() => {
    const handleOpen = (event: Event) => {
      const customEvent = event as CustomEvent<TelephonyDrawerOpenDetail>
      if (!customEvent.detail?.url) return

      setEmbeddedWebphoneUrl('')
      setDrawerState(customEvent.detail)
      setShow(true)
      setCollapsed(false)
    }

    const handleUpdate = (event: Event) => {
      const customEvent = event as CustomEvent<{diagnostics?: TelephonyDrawerDiagnostics}>
      if (!customEvent.detail?.diagnostics) return

      setDrawerState((current) => ({
        ...current,
        diagnostics: {
          ...(current.diagnostics || {}),
          ...customEvent.detail.diagnostics,
        },
      }))
    }

    const handleClose = () => {
      setEmbeddedWebphoneUrl('')
      setCollapsed(false)
      setShow(false)
    }

    const handleToggle = () => {
      if (!show) {
        if (drawerState.url) {
          setShow(true)
          setCollapsed(false)
        }
        return
      }

      setCollapsed((current) => !current)
    }

    const handleMessage = (event: MessageEvent) => {
      const data = event.data as {type?: string; webphoneUrl?: string} | undefined
      if (!data || data.type !== 'POLICYTOOLS_VICIDIAL_WEBPHONE_READY' || !data.webphoneUrl) {
        return
      }

      setEmbeddedWebphoneUrl(data.webphoneUrl)
      setDrawerState((current) => ({
        ...current,
        diagnostics: {
          ...(current.diagnostics || {}),
          message: 'VICIdial connected. Loading embedded webphone...',
        },
      }))
    }

    window.addEventListener(TELEPHONY_DRAWER_OPEN_EVENT, handleOpen as EventListener)
    window.addEventListener(TELEPHONY_DRAWER_UPDATE_EVENT, handleUpdate as EventListener)
    window.addEventListener(TELEPHONY_DRAWER_CLOSE_EVENT, handleClose as EventListener)
    window.addEventListener(TELEPHONY_DRAWER_TOGGLE_EVENT, handleToggle as EventListener)
    window.addEventListener('message', handleMessage)

    const channel = new BroadcastChannel('policytools_vicidial_channel')
    channel.onmessage = (event) => {
      if (event.data === 'CLOSE_WEBPHONE') {
        closeTrackedExternalWindow()
        setEmbeddedWebphoneUrl('')
        setCollapsed(false)
        setShow(false)
      }
    }

    return () => {
      window.removeEventListener(TELEPHONY_DRAWER_OPEN_EVENT, handleOpen as EventListener)
      window.removeEventListener(TELEPHONY_DRAWER_UPDATE_EVENT, handleUpdate as EventListener)
      window.removeEventListener(TELEPHONY_DRAWER_CLOSE_EVENT, handleClose as EventListener)
      window.removeEventListener(TELEPHONY_DRAWER_TOGGLE_EVENT, handleToggle as EventListener)
      window.removeEventListener('message', handleMessage)
      channel.close()
    }
  }, [drawerState.url, show])

  useEffect(() => {
    publishDrawerState(show, show && !collapsed, drawerState.provider)
  }, [collapsed, drawerState.provider, publishDrawerState, show])

  const effectiveVicidialStatus = useMemo(() => {
    return String(drawerState.diagnostics?.status || telephony.vicidialSession.status || '')
      .trim()
      .toUpperCase()
  }, [drawerState.diagnostics?.status, telephony.vicidialSession.status])

  const isPausedState = effectiveVicidialStatus === 'PAUSED'
  const isReadyState =
    Boolean(telephony.vicidialSession.activeSession || drawerState.diagnostics?.session_id) &&
    !isPausedState

  const hasStructuredDiagnostics = Boolean(
    drawerState.diagnostics?.agent_user ||
      drawerState.diagnostics?.status ||
      drawerState.diagnostics?.campaign_id ||
      drawerState.diagnostics?.pause_code ||
      drawerState.diagnostics?.session_id ||
      drawerState.diagnostics?.phone_number
  )

  return (
    <Offcanvas
      show={show}
      onHide={() => setCollapsed(true)}
      placement='end'
      backdrop={!collapsed}
      style={{
        width: drawerState.width || '460px',
        transform: collapsed ? 'translateX(100%)' : undefined,
        opacity: collapsed ? 0 : 1,
        pointerEvents: collapsed ? 'none' : 'auto',
        transition: 'transform 0.3s ease, opacity 0.2s ease',
      }}
    >
      <Offcanvas.Header closeButton>
        <div className='d-flex flex-column'>
          <Offcanvas.Title>{drawerState.title || 'Webphone'}</Offcanvas.Title>
          {drawerState.warning && (
            <span className='text-muted fs-8 mt-1'>{drawerState.warning}</span>
          )}
        </div>
      </Offcanvas.Header>
      <Offcanvas.Body className='p-0 d-flex flex-column'>
        <div className='border-bottom p-3 d-flex justify-content-end gap-2'>
          {drawerState.provider === 'VICIDIAL' && (
            <Button
              variant='light-danger'
              size='sm'
              onClick={handleVicidialLogout}
              disabled={disconnecting}
            >
              {disconnecting ? 'Logging out...' : 'Logout'}
            </Button>
          )}
          <Button
            variant='light-primary'
            size='sm'
            onClick={openInNewTab}
            disabled={!drawerState.url || disconnecting}
          >
            {shouldForceExternalOpen ? 'Open secure webphone' : 'Open in new tab'}
          </Button>
        </div>
        {shouldForceExternalOpen && (
          <Alert variant='warning' className='m-3 mb-0'>
            Your current app session is running over HTTP. ViciPhone rejects embedded access from
            non-encrypted referrers like <code>localhost</code>. Use <strong>Open secure webphone</strong>{' '}
            to continue in a separate HTTPS tab.
          </Alert>
        )}
        {drawerState.diagnostics && (
          <div className='border-bottom px-3 py-2 bg-light'>
            {drawerState.diagnostics.message && (
              <div className='fw-semibold mb-2'>{drawerState.diagnostics.message}</div>
            )}
            {hasStructuredDiagnostics ? (
              <div className='d-flex flex-wrap gap-2 fs-8'>
                {drawerState.diagnostics.agent_user && (
                  <Badge bg='light' text='dark' className='border'>
                    Agent: {drawerState.diagnostics.agent_user}
                  </Badge>
                )}
                {drawerState.diagnostics.status && (
                  <Badge
                    bg={isPausedState ? 'warning' : isReadyState ? 'success' : 'secondary'}
                    text={isPausedState ? 'dark' : 'white'}
                  >
                    Status: {drawerState.diagnostics.status}
                  </Badge>
                )}
                {drawerState.diagnostics.campaign_id && (
                  <Badge bg='light' text='dark' className='border'>
                    Campaign: {drawerState.diagnostics.campaign_id}
                  </Badge>
                )}
                {drawerState.diagnostics.pause_code && (
                  <Badge bg='light' text='dark' className='border'>
                    Pause code: {drawerState.diagnostics.pause_code}
                  </Badge>
                )}
                {drawerState.diagnostics.session_id && (
                  <Badge bg='light' text='dark' className='border'>
                    Session: {drawerState.diagnostics.session_id}
                  </Badge>
                )}
                {drawerState.diagnostics.phone_number && (
                  <Badge bg='light' text='dark' className='border'>
                    Phone: {drawerState.diagnostics.phone_number}
                  </Badge>
                )}
              </div>
            ) : null}
            {!hasStructuredDiagnostics && drawerState.diagnostics.raw_response && (
              <pre
                className='mt-2 mb-0 p-2 rounded bg-white border'
                style={{
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  maxHeight: '120px',
                  overflow: 'auto',
                  fontSize: '11px',
                }}
              >
                {drawerState.diagnostics.raw_response}
              </pre>
            )}
          </div>
        )}
        {canOperateVicidial && (
          <div className='border-bottom px-3 py-3 bg-white'>
            <div className='fw-semibold mb-2'>Agent Controls</div>
            <div className='d-flex flex-column gap-2'>
              <div className='d-flex gap-2 flex-wrap'>
                <Form.Control
                  size='sm'
                  placeholder='Pause code'
                  value={pauseCodeInput}
                  maxLength={6}
                  onChange={(event) => setPauseCodeInput(event.target.value.toUpperCase())}
                  style={{maxWidth: '120px'}}
                  disabled={disconnecting || telephony.vicidialSession.connecting}
                />
                <Button
                  variant={isPausedState ? 'warning' : 'outline-warning'}
                  size='sm'
                  onClick={handlePauseAgent}
                  active={isPausedState}
                  disabled={
                    disconnecting ||
                    telephony.vicidialSession.connecting ||
                    runningAction === 'pause' ||
                    !telephony.vicidialSession.activeSession
                  }
                >
                  {runningAction === 'pause' ? 'Pausing...' : 'Pause'}
                </Button>
                <Button
                  variant={isReadyState ? 'success' : 'outline-success'}
                  size='sm'
                  onClick={handleResumeAgent}
                  active={isReadyState}
                  disabled={
                    disconnecting ||
                    telephony.vicidialSession.connecting ||
                    runningAction === 'resume' ||
                    !telephony.vicidialSession.activeSession
                  }
                >
                  {runningAction === 'resume' ? 'Resuming...' : 'Ready'}
                </Button>
              </div>
              <div className='d-flex gap-2 flex-wrap'>
                <Form.Control
                  size='sm'
                  placeholder='DTMF digits, #, *, comma pause'
                  value={dtmfDigits}
                  onChange={(event) => setDtmfDigits(event.target.value)}
                  style={{minWidth: '220px', flex: '1 1 220px'}}
                  disabled={disconnecting || telephony.vicidialSession.connecting}
                />
                <Button
                  variant='outline-primary'
                  size='sm'
                  onClick={() => runVicidialDrawerAction('dtmf', handleSendDtmf)}
                  disabled={
                    disconnecting ||
                    telephony.vicidialSession.connecting ||
                    runningAction === 'dtmf' ||
                    !telephony.vicidialSession.activeSession
                  }
                >
                  {runningAction === 'dtmf' ? 'Sending...' : 'Send DTMF'}
                </Button>
              </div>
              <div className='d-flex gap-2 flex-wrap'>
                <Form.Control
                  size='sm'
                  placeholder='Blind transfer number'
                  value={transferPhoneNumber}
                  onChange={(event) => setTransferPhoneNumber(event.target.value)}
                  style={{minWidth: '220px', flex: '1 1 220px'}}
                  disabled={disconnecting || telephony.vicidialSession.connecting}
                />
                <Button
                  variant='outline-primary'
                  size='sm'
                  onClick={() => runVicidialDrawerAction('transfer', handleTransferCall)}
                  disabled={
                    disconnecting ||
                    telephony.vicidialSession.connecting ||
                    runningAction === 'transfer' ||
                    !telephony.vicidialSession.activeSession
                  }
                >
                  {runningAction === 'transfer' ? 'Sending...' : 'Blind Transfer'}
                </Button>
              </div>
              <div className='d-flex gap-2 flex-wrap'>
                <Button
                  variant='outline-secondary'
                  size='sm'
                  onClick={() =>
                    runVicidialDrawerAction('park', () =>
                      telephony.parkVicidialCall('PARK_CUSTOMER')
                    )
                  }
                  disabled={
                    disconnecting ||
                    telephony.vicidialSession.connecting ||
                    runningAction === 'park' ||
                    !telephony.vicidialSession.activeSession
                  }
                >
                  {runningAction === 'park' ? 'Parking...' : 'Park Customer'}
                </Button>
                <Button
                  variant='outline-secondary'
                  size='sm'
                  onClick={() =>
                    runVicidialDrawerAction('grab', () =>
                      telephony.parkVicidialCall('GRAB_CUSTOMER')
                    )
                  }
                  disabled={
                    disconnecting ||
                    telephony.vicidialSession.connecting ||
                    runningAction === 'grab' ||
                    !telephony.vicidialSession.activeSession
                  }
                >
                  {runningAction === 'grab' ? 'Grabbing...' : 'Grab Customer'}
                </Button>
              </div>
            </div>
          </div>
        )}
        {drawerState.url && !shouldForceExternalOpen ? (
          <>
            <iframe
              src={drawerState.url}
              title={drawerState.title || 'Webphone'}
              allow='microphone; autoplay'
              referrerPolicy='no-referrer'
              style={
                embeddedWebphoneUrl && isVicidialAutologinBridge
                  ? {
                      border: 0,
                      width: '1px',
                      height: '1px',
                      opacity: 0,
                      pointerEvents: 'none',
                      position: 'absolute',
                    }
                  : {border: 0, width: '100%', height: '100%'}
              }
            />
            {embeddedWebphoneUrl && isVicidialAutologinBridge ? (
              <iframe
                src={embeddedWebphoneUrl}
                title='VICIdial Embedded Webphone'
                allow='microphone; autoplay'
                referrerPolicy='no-referrer'
                style={{border: 0, width: '100%', height: '100%'}}
              />
            ) : null}
          </>
        ) : null}
      </Offcanvas.Body>
    </Offcanvas>
  )
}
